import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Gavel, ImageOff, Store, User, Clock, AlertTriangle, X } from 'lucide-react';

const money = (n) => `$${(n || 0).toFixed(2)}`;

const Fact = ({ label, children, testId }) => (
  <div className="min-w-0" data-testid={testId}>
    <div className="text-[10px] uppercase tracking-[0.12em] text-gray-500">{label}</div>
    <div className="text-sm text-gray-100 break-words">{children}</div>
  </div>
);

/**
 * Ruling screen for returns a buyer has brought to us — the only point at which the platform
 * has any say (owner, Jun 2026). Photos are mandatory before a buyer can escalate, so they are
 * the first thing on the card, side by side and full size on click.
 */
export default function EscalationQueue({ onRuled }) {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes]     = useState({});
  const [busyId, setBusyId]   = useState(null);
  const [lightbox, setLight]  = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/marketplace/returns`, {
        params: { status: 'escalated' },
        headers: { Authorization: `Bearer ${token}` },
      });
      setRows((res.data || []).slice().sort((a, b) => {
        // Longest-waiting first — a ruling queue should surface what is rotting, not the newest.
        const at = a.escalated_at || a.created_at || '';
        const bt = b.escalated_at || b.created_at || '';
        return at < bt ? -1 : at > bt ? 1 : 0;
      }));
    } catch (e) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const rule = async (id, approve) => {
    setBusyId(id);
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/admin/marketplace/returns/${id}/override`,
        { approve, note: notes[id] || null },
        { headers: { Authorization: `Bearer ${token}` } });
      await load();
      onRuled?.();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Could not rule on this claim');
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return <div className="py-10 text-center text-gray-400" data-testid="admin-escalations-loading">Loading…</div>;
  }

  if (rows.length === 0) {
    return (
      <Card className="bg-white/5 border-white/10">
        <CardContent className="py-12 text-center" data-testid="admin-escalations-empty">
          <Gavel className="h-7 w-7 mx-auto mb-3 text-gray-600" />
          <p className="text-gray-300">Nothing waiting on a ruling.</p>
          <p className="text-xs text-gray-500 mt-1">
            Stores answer their own returns. A claim only reaches you when they decline it, or
            when they let seven days go by without a word.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="admin-escalations-list">
      {rows.map((r) => {
        const photos = r.evidence || [];
        const items = r.receipt?.items || [];
        const consumable = items.some(i => i.consumable);
        const policy = r.policy_snapshot || {};
        return (
          <Card key={r.id} className="bg-white/5 border-white/10" data-testid={`admin-escalation-${r.order_number}`}>
            <CardContent className="p-4">
              <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)] gap-5">

                {/* Evidence */}
                <div>
                  <div className="text-[10px] uppercase tracking-[0.12em] text-gray-500 mb-2">
                    What the buyer sent ({photos.length})
                  </div>
                  {photos.length === 0 ? (
                    <div
                      className="flex items-center gap-2 text-xs text-gray-500 border border-dashed border-white/15 rounded-xl py-8 justify-center"
                      data-testid="admin-escalation-no-photos"
                    >
                      <ImageOff className="h-4 w-4" /> No photos on this claim
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 xl:grid-cols-3 gap-2" data-testid="admin-escalation-photos">
                      {photos.map((url, i) => (
                        <button
                          key={`${url}-${i}`}
                          type="button"
                          onClick={() => setLight(url)}
                          className="block rounded-xl overflow-hidden border border-white/10 hover:border-white/40 transition-colors"
                          data-testid={`admin-escalation-photo-${i}`}
                        >
                          <img src={url} alt={`Buyer photo ${i + 1}`} className="w-full h-32 object-cover" />
                        </button>
                      ))}
                    </div>
                  )}

                  <div className="mt-3 space-y-2">
                    <Fact label="Their words" testId="admin-escalation-reason">{r.reason || '—'}</Fact>
                    {r.buyer_escalation_note && (
                      <Fact label="What they added when escalating">{r.buyer_escalation_note}</Fact>
                    )}
                    {r.vendor_escalation_note && (
                      <Fact label="Why the store brought it to us" testId="admin-escalation-vendor-escalation-note">
                        {r.vendor_escalation_note}
                      </Fact>
                    )}
                    {r.vendor_note && (
                      <Fact label="The store's reason for declining" testId="admin-escalation-vendor-note">
                        {r.vendor_note}
                      </Fact>
                    )}
                    {!r.vendor_note && !r.vendor_escalation_note && (
                      <Fact label="The store's reason for declining">
                        <span className="text-amber-200">Never answered</span>
                      </Fact>
                    )}
                  </div>
                </div>

                {/* The facts and the ruling */}
                <div className="space-y-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-mono text-sm text-white">{r.order_number}</span>
                    <Badge className="text-[10px] border bg-orange-900/40 text-orange-200 border-orange-700/50">
                      escalated
                    </Badge>
                    {r.outside_policy && (
                      <Badge className="text-[10px] border bg-gray-800 text-gray-300 border-gray-600">
                        outside store policy
                      </Badge>
                    )}
                    {consumable && (
                      <Badge className="text-[10px] border bg-red-900/40 text-red-200 border-red-700/50"
                             data-testid="admin-escalation-consumable-badge">
                        consumable
                      </Badge>
                    )}
                    <span className="ml-auto text-base text-white">{money(r.amount)}</span>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-3">
                    <Fact label="Store"><span className="flex items-center gap-1.5"><Store className="h-3.5 w-3.5 text-gray-500" />{r.receipt?.vendor_store_name || '—'}</span></Fact>
                    <Fact label="Brought to us by" testId="admin-escalation-raised-by">
                      {r.escalated_by === 'vendor' ? 'The store' : 'The buyer'}
                    </Fact>
                    <Fact label="Buyer"><span className="flex items-center gap-1.5"><User className="h-3.5 w-3.5 text-gray-500" />{r.receipt?.buyer_name || '—'}</span></Fact>
                    <Fact label="What went wrong" testId="admin-escalation-issue">
                      {r.issue_label || <span className="text-gray-500">Not recorded</span>}
                    </Fact>
                    <Fact label="What they want" testId="admin-escalation-wanted">
                      {r.resolution === 'replacement' ? 'A replacement' : 'A refund'}
                    </Fact>
                    <Fact label="Waiting">
                      <span className="flex items-center gap-1.5">
                        <Clock className="h-3.5 w-3.5 text-gray-500" />
                        {r.days_open ?? 0} day(s) since they asked
                        {r.days_escalated != null && ` · ${r.days_escalated} with us`}
                      </span>
                    </Fact>
                    <Fact label="The store's policy at the time">
                      {policy.accepts_returns
                        ? `${policy.window_days || 0}-day returns · shipping paid by ${policy.return_shipping_paid_by || 'buyer'}`
                        : 'No returns accepted'}
                    </Fact>
                  </div>

                  {r.policy_block && (
                    <p className="text-xs text-gray-400" data-testid="admin-escalation-policy-block">
                      Store policy said: {r.policy_block}
                    </p>
                  )}

                  {items.length > 0 && (
                    <div className="flex flex-wrap gap-1.5" data-testid="admin-escalation-items">
                      {items.map((i, idx) => (
                        <span
                          key={`${i.product_id}-${idx}`}
                          className="text-[11px] px-2 py-1 rounded-lg bg-white/5 border border-white/10 text-gray-200"
                        >
                          {i.title} × {i.qty || 1}
                          {i.consumable && <span className="text-red-300"> · consumable</span>}
                          {i.final_sale && <span className="text-gray-400"> · final sale</span>}
                        </span>
                      ))}
                    </div>
                  )}

                  {consumable && (
                    <div
                      className="flex gap-2 text-xs text-red-200 bg-red-950/30 border border-red-900/40 rounded-xl px-3 py-2"
                      data-testid="admin-escalation-consumable-warning"
                    >
                      <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                      <span>
                        This claim includes a consumable. A consumable can&apos;t be returned or
                        resold, so a forced refund is a total loss to the store — the documents say
                        the platform will not require one. Replacement is the store&apos;s call.
                      </span>
                    </div>
                  )}

                  <div className="pt-1 space-y-2">
                    <Input
                      value={notes[r.id] || ''}
                      onChange={(e) => setNotes({ ...notes, [r.id]: e.target.value })}
                      placeholder="Your ruling, in a sentence (sent to the buyer and the store)"
                      className="bg-white/5 border-white/10 text-white"
                      data-testid="admin-escalation-note"
                    />
                    <div className="flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        disabled={busyId === r.id}
                        onClick={() => rule(r.id, true)}
                        data-testid="admin-escalation-refund-btn"
                      >
                        Refund the buyer ({money(r.amount)})
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={busyId === r.id}
                        onClick={() => rule(r.id, false)}
                        className="border-slate-600 text-slate-200 hover:text-white hover:bg-slate-700/60"
                        data-testid="admin-escalation-uphold-btn"
                      >
                        Side with the store
                      </Button>
                      {r.vendor_email && (
                        <a
                          href={`mailto:${r.vendor_email}?subject=Return ${r.order_number}`}
                          className="text-xs text-gray-400 hover:text-white self-center underline"
                          data-testid="admin-escalation-email-store"
                        >
                          Email the store first
                        </a>
                      )}
                    </div>
                    <p className="text-[11px] text-gray-500">
                      A refund you direct is charged back to the store — from their share of that
                      order if it is still held, otherwise against their next payout.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}

      {lightbox && (
        <div
          className="fixed inset-0 z-[200] bg-black/90 flex items-center justify-center p-6"
          onClick={() => setLight(null)}
          data-testid="admin-escalation-lightbox"
        >
          <button
            type="button"
            className="absolute top-5 right-6 text-white/70 hover:text-white"
            onClick={() => setLight(null)}
            data-testid="admin-escalation-lightbox-close"
          >
            <X className="h-6 w-6" />
          </button>
          <img src={lightbox} alt="Buyer evidence" className="max-h-full max-w-full rounded-xl" />
        </div>
      )}
    </div>
  );
}
